import React, { useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { SBSection, SourceBook, SpecialSectionType, SPECIAL_SECTION_TYPES, isSpecialSectionType } from "@dndModels";

import API from "@utilities/smart_api";
import FlexBox from "@components/layout/FlexBox";
import DisplayStringWithReplacementsRecursive from "@dndComponents/display/DisplayStringWithReplacementsRecursive";

import { analyzePdf, buildSections, countSections, ImportedSection, PdfAnalysis } from "./pdf_structure";
import { extractPdfText } from "./pdf_extract";

const api = API.getInstance();

// How many sections get created at once.
const CONCURRENCY = 4;

interface Props {
  source_book: SourceBook;
  // Called once the sections have been created and added to the source book.
  onImported: () => void;
}

// Sections are identified by where they are in the tree, like "3.0.12".
const pathKey = (path: number[]) => path.join(".");

// A copy of the set with key added (or removed).
const toggled = (set: Set<string>, key: string, add: boolean) => {
  const next = new Set(set);
  if (add) {
    next.add(key);
  } else {
    next.delete(key);
  }
  return next;
};

/**
 * Imports a whole PDF (like the SRD) into a source book as sections. The PDF is read in the
 * browser, its headings become the section structure, and the text between them becomes
 * each section's content. Before importing, the heading levels can be adjusted, and sections
 * can be left out or given a special type (whose subsections then aren't imported).
 */
export default function SourceBookPdfImporter({ source_book, onImported }: Props) {
  const [file_name, setFileName] = useState("");
  const [reading, setReading] = useState<{ done: number, total: number } | null>(null);
  const [error, setError] = useState("");
  const [analysis, setAnalysis] = useState<PdfAnalysis | null>(null);
  const [levels, setLevels] = useState<{ [style_key: string]: number }>({});
  // Choices about sections, by pathKey. They're cleared when the heading levels change,
  // since the tree changes with them.
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [special_types, setSpecialTypes] = useState<{ [path: string]: SpecialSectionType }>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [previewing, setPreviewing] = useState<ImportedSection | null>(null);
  const [importing, setImporting] = useState<{ done: number, total: number, failed: number } | null>(null);
  const [imported, setImported] = useState(false);
  const file_input = useRef<HTMLInputElement | null>(null);

  const sections = useMemo(() => analysis ? buildSections(analysis, levels) : [], [analysis, levels]);

  const readFile = async (file: File) => {
    setFileName(file.name);
    setError("");
    setAnalysis(null);
    setImported(false);
    setPreviewing(null);
    setReading({ done: 0, total: 0 });
    try {
      const pages = await extractPdfText(await file.arrayBuffer(), (done, total) => setReading({ done, total }));
      const result = analyzePdf(pages);
      setAnalysis(result);
      setLevels(Object.fromEntries(result.styles.map(s => [s.key, s.level])));
      setExcluded(new Set());
      setSpecialTypes({});
      setExpanded(new Set());
    } catch (ex) {
      console.error(ex);
      setError(`Couldn't read ${file.name}: ${ex instanceof Error ? ex.message : ex}`);
    } finally {
      setReading(null);
    }
  };

  const changeLevel = (style_key: string, level: number) => {
    setLevels(prev => ({ ...prev, [style_key]: level }));
    setExcluded(new Set());
    setSpecialTypes({});
    setExpanded(new Set());
    setPreviewing(null);
  };

  // The sections that will be created: included ones, and not under a special section.
  const willImport = (section: ImportedSection, path: number[]): number => {
    const key = pathKey(path);
    if (excluded.has(key)) return 0;
    if ((special_types[key] ?? "None") !== "None") return 1;
    return 1 + section.children.reduce((total, child, i) => total + willImport(child, [...path, i]), 0);
  };
  const import_count = sections.reduce((total, s, i) => total + willImport(s, [i]), 0);

  const importSections = async () => {
    type Job = { imported: ImportedSection, path: number[], parent: SBSection | null, position: number };
    const new_roots: SBSection[] = [];
    let level: Job[] = [];
    let root_position = source_book.sections.length;
    sections.forEach((s, i) => {
      if (!excluded.has(pathKey([i]))) {
        level.push({ imported: s, path: [i], parent: null, position: root_position++ });
      }
    });
    let done = 0;
    let failed = 0;
    setImporting({ done, total: import_count, failed });

    const createOne = async (job: Job): Promise<Job[]> => {
      const key = pathKey(job.path);
      const special_type = special_types[key] ?? "None";
      const section = new SBSection();
      section.name = job.imported.name;
      section.content = job.imported.content;
      section.special_type = special_type;
      section.parent_type = job.parent ? "SBSection" : "Source Book";
      section.parent_id = job.parent ? job.parent._id : source_book._id;
      section.position = job.position;
      let created = false;
      try {
        const res = await api.createObject("sb_section", section);
        created = !!(res && res.body.id);
      } catch (ex) {
        console.error(ex);
      }
      done++;
      if (!created) {
        // Its subsections can't be made without it, so they count as failed (and done) too.
        const lost = willImport(job.imported, job.path);
        failed += lost;
        done += lost - 1;
        setImporting({ done, total: import_count, failed });
        return [];
      }
      setImporting({ done, total: import_count, failed });
      if (job.parent) {
        job.parent.subsections.push(section);
      } else {
        new_roots.push(section);
      }
      if (special_type !== "None") {
        return [];
      }
      let position = 0;
      const children: Job[] = [];
      job.imported.children.forEach((child, i) => {
        const path = [...job.path, i];
        if (!excluded.has(pathKey(path))) {
          children.push({ imported: child, path, parent: section, position: position++ });
        }
      });
      return children;
    };

    // Parents before their children (children need their parent's id), a few at a time.
    while (level.length > 0) {
      const next: Job[] = [];
      for (let i = 0; i < level.length; i += CONCURRENCY) {
        const results = await Promise.all(level.slice(i, i + CONCURRENCY).map(createOne));
        results.forEach(children => next.push(...children));
      }
      level = next;
    }

    // They were created a few at a time, so put them back in order before saving the structure.
    const sortTree = (list: SBSection[]) => {
      list.sort((a, b) => a.position - b.position);
      list.forEach(s => sortTree(s.subsections));
    };
    sortTree(new_roots);
    source_book.sections = [...source_book.sections, ...new_roots];
    await api.updateObject("source_book", source_book, ["sections"]);
    setImported(true);
    onImported();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const busy = reading !== null || (importing !== null && !imported);

  const renderStyles = () => {
    if (!analysis) return null;
    const body = analysis.styles.find(s => s.key === analysis.body_key);
    // Bold styles bigger than the body text are the ones that could be headings.
    const options = analysis.styles.filter(s =>
      s.level > 0 || (/bold|semibold|demi|black|heavy/i.test(s.font) && body && s.size > body.size)
    );
    return (
      <Paper variant="outlined" style={{ padding: 8, marginTop: 8 }}>
        <Typography variant="subtitle2">Heading Styles</Typography>
        <Typography variant="body2" className="gray">
          Lines in a heading style start a new section at that level. Body text is { analysis.body_key }.
        </Typography>
        { options.map(style => (
          <FlexBox key={style.key} direction="row" style={{ alignItems: "center", gap: 8, marginTop: 4 }}>
            <Select size="small" value={levels[style.key] ?? 0} disabled={busy}
              onChange={e => changeLevel(style.key, Number(e.target.value))} style={{ minWidth: 150 }}>
              <MenuItem value={0}>Not a heading</MenuItem>
              { [1, 2, 3, 4, 5, 6].map(l => <MenuItem key={l} value={l}>Level {l}</MenuItem>) }
            </Select>
            <span style={{ flex: 1 }}>
              <strong>{ style.font } { style.size }</strong>
              <span className="small_label gray"> ({ style.lines } lines{ style.suggested_level !== (levels[style.key] ?? 0) ? `, suggested ${style.suggested_level === 0 ? "not a heading" : `level ${style.suggested_level}`}` : "" }): { style.samples.join(" · ") }</span>
            </span>
          </FlexBox>
        ))}
      </Paper>
    );
  };

  const renderNode = (section: ImportedSection, path: number[], depth: number, dimmed: boolean): React.ReactNode => {
    const key = pathKey(path);
    const is_excluded = excluded.has(key);
    const special_type = special_types[key] ?? "None";
    const is_open = expanded.has(key);
    const children_dimmed = dimmed || is_excluded || special_type !== "None";
    return (
      <div key={key}>
        <FlexBox direction="row" style={{ alignItems: "center", marginLeft: depth * 20, opacity: dimmed || is_excluded ? 0.5 : 1 }}>
          { section.children.length > 0
            ? <IconButton size="small" onClick={() => setExpanded(prev => toggled(prev, key, !prev.has(key)))}>
                { is_open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" /> }
              </IconButton>
            : <span style={{ width: 30 }} />
          }
          <Checkbox size="small" checked={!is_excluded} disabled={dimmed || busy || imported}
            onChange={e => setExcluded(prev => toggled(prev, key, !e.target.checked))} />
          <span className="clickable" style={{ flex: 1, fontWeight: previewing === section ? 600 : 400 }} onClick={() => setPreviewing(section)}>
            { section.name }
            <span className="small_label gray">
              {" "}p{ section.page } · { section.content.length.toLocaleString() } chars{ section.children.length > 0 ? ` · ${countSections(section.children)} subsections` : "" }
            </span>
          </span>
          <Select size="small" value={special_type} disabled={dimmed || is_excluded || busy || imported} style={{ minWidth: 170, marginLeft: 8 }}
            onChange={e => {
              const value = String(e.target.value);
              if (isSpecialSectionType(value)) {
                setSpecialTypes(prev => ({ ...prev, [key]: value }));
              }
            }}>
            { SPECIAL_SECTION_TYPES.map(t => <MenuItem key={t} value={t}>{ t === "None" ? "Imported text" : t }</MenuItem>) }
          </Select>
        </FlexBox>
        { is_open && section.children.map((child, i) => renderNode(child, [...path, i], depth + 1, children_dimmed)) }
      </div>
    );
  };

  return (
    <FlexBox direction="column" style={{ gap: 8 }}>
      <Typography variant="body2">
        Choose a PDF to import into <strong>{ source_book.name }</strong>. It&apos;s read here in the browser, then you can
        check the headings and sections before anything is created. New sections are added after the ones it already has.
      </Typography>
      <FlexBox direction="row" style={{ alignItems: "center", gap: 8 }}>
        <input ref={file_input} type="file" accept="application/pdf,.pdf" style={{ display: "none" }}
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) readFile(file);
            e.target.value = "";
          }} />
        <Button variant="contained" size="small" disabled={busy} onClick={() => file_input.current?.click()}>
          { analysis ? "Choose a Different PDF" : "Choose PDF" }
        </Button>
        { file_name && <span>{ file_name }</span> }
      </FlexBox>
      { reading &&
        <FlexBox direction="column">
          <LinearProgress variant={reading.total > 0 ? "determinate" : "indeterminate"} value={reading.total > 0 ? reading.done / reading.total * 100 : 0} />
          <span className="small_label">{ reading.total > 0 ? `Reading page ${reading.done} of ${reading.total}...` : "Opening..." }</span>
        </FlexBox>
      }
      { error && <Typography color="error" variant="body2">{ error }</Typography> }
      { renderStyles() }
      { analysis &&
        <Grid container spacing={1}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper variant="outlined" style={{ padding: 8, maxHeight: "70vh", overflowY: "auto" }}>
              <Typography variant="subtitle2">Sections ({ countSections(sections).toLocaleString() })</Typography>
              <Typography variant="body2" className="gray" gutterBottom>
                Uncheck anything you don&apos;t want. Giving a section a special type keeps its own text, but its subsections
                aren&apos;t imported, since it fills itself in from your data.
              </Typography>
              { sections.map((s, i) => renderNode(s, [i], 0, false)) }
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper variant="outlined" style={{ padding: 8, maxHeight: "70vh", overflowY: "auto" }}>
              { previewing
                ? <>
                    <Typography variant="h6">{ previewing.name }</Typography>
                    { previewing.content
                      ? <DisplayStringWithReplacementsRecursive display_string={previewing.content} />
                      : <span className="gray">No text of its own.</span>
                    }
                  </>
                : <span className="gray">Click a section to see its text.</span>
              }
            </Paper>
          </Grid>
        </Grid>
      }
      { analysis &&
        <FlexBox direction="column" style={{ gap: 4 }}>
          { importing &&
            <>
              <LinearProgress variant="determinate" value={importing.total > 0 ? importing.done / importing.total * 100 : 100} />
              <span className="small_label">
                { imported
                  ? `Imported ${(importing.total - importing.failed).toLocaleString()} sections${importing.failed > 0 ? `, ${importing.failed} failed` : ""}.`
                  : `Creating section ${importing.done.toLocaleString()} of ${importing.total.toLocaleString()}...` }
              </span>
            </>
          }
          <FlexBox direction="row">
            <Button variant="contained" disabled={busy || imported || import_count === 0} onClick={importSections}>
              Import { import_count.toLocaleString() } Sections
            </Button>
          </FlexBox>
        </FlexBox>
      }
    </FlexBox>
  );
}
